import axios from "axios";
import { basicURL } from "../../apiManager/APIConfig";
import { CallAPI } from "../../apiManager/ApiCall";
import {
  aiAskURL,
  aiGenerateQuestionURL,
  aiGenerateQuestionsFromMaterialURL,
  aiImproveTextURL,
  aiThreadMessagesURL,
} from "../../apiManager/APIConfig";
import { store } from "../store";

/**
 * Generate a quiz question suggestion using AI.
 * @param {{ prompt?: string, title?: string, slide_context?: string, question_type?: string }} payload
 * @returns {Promise<{ question_content: string, title: string, difficulty: string, answers: Array<{ answer_text: string, is_correct: boolean }> }>}
 */
export const generateQuestionCall = (payload) =>
  CallAPI({
    url: aiGenerateQuestionURL,
    method: "POST",
    body: {
      prompt: payload.prompt || "",
      title: payload.title || "",
      slide_context: payload.slide_context || "",
      question_type: payload.question_type || "text_only",
    },
  });

export const improveAdminTextCall = ({ text = "", topic = "", mode = "improve", context = "description" }) =>
  CallAPI({
    url: aiImproveTextURL,
    method: "POST",
    body: {
      text,
      topic,
      mode,
      context,
    },
  });

export const askAIChatCall = async (formData, setProgress = () => {}) => {
  const access_token = store.getState().auth?.access_token;
  const authHeader = access_token?.startsWith("Bearer ")
    ? access_token
    : `Bearer ${access_token}`;

  const response = await axios.post(`${basicURL}${aiAskURL}`, formData, {
    headers: {
      Authorization: authHeader,
    },
    onUploadProgress: (progressEvent) => {
      if (progressEvent?.total) {
        setProgress(progressEvent.loaded / progressEvent.total);
      }
    },
  });
  return response?.data;
};

/** POST /ai/tutor-chat — same Ask AI path the mobile app uses. */
export const askTutorChatCall = async (formData) => {
  const access_token = store.getState().auth?.access_token;
  const authHeader = access_token?.startsWith("Bearer ")
    ? access_token
    : `Bearer ${access_token}`;

  try {
    const response = await axios.post(`${basicURL}${aiAskURL}/tutor-chat`, formData, {
      headers: { Authorization: authHeader },
    });
    return response?.data;
  } catch (err) {
    if (err?.response?.data) return err.response.data;
    return {
      status: err?.response?.status || 0,
      message: err?.message || "The question could not be sent.",
    };
  }
};

export const getAIMessagesByThreadCall = (threadId) =>
  CallAPI({
    url: `${aiThreadMessagesURL}/${threadId}/messages`,
    method: "GET",
  });

export const getAllAIThreadsCall = (locale = "en") =>
  CallAPI({
    url: `${aiThreadMessagesURL}/my?locale=${encodeURIComponent(locale)}`,
    method: "GET",
  });

/**
 * Generate a batch of multiple-choice quiz questions from an uploaded
 * material file (PDF / PPT / image / audio / generic doc) OR pasted text.
 *
 * Caller builds a FormData and provides any subset of these fields:
 *   file?         — File / Blob
 *   text?         — pasted source text (used if no file)
 *   count         — number of questions (1-30, default 5)
 *   lang          — locale (default "en")
 *   topic?        — extra hint for the model
 *   category?     — subject hint ("histology" | "embryology" | "anatomy" | …)
 *   source_type?  — explicit type override
 *   question_type?— "text_only" | "image_based" | "slide_referenced"
 *
 * Resolves to:
 *   { status, message, sourceMeta, questions: [
 *       { id, title, difficulty, question_content,
 *         answers: [{ answer_text, is_correct }] (length 4) }
 *     ] }
 *
 * Uses axios directly (not the XHR-based CallAPI) so callers can stream
 * upload progress and so the browser sets the multipart boundary itself.
 * The OpenAI call can take 30-90s for large materials, so the timeout is
 * generous.
 */
export const generateQuestionsFromMaterialCall = async (
  formData,
  setProgress = () => {},
) => {
  const access_token = store.getState().auth?.access_token;
  const authHeader = access_token?.startsWith("Bearer ")
    ? access_token
    : `Bearer ${access_token}`;

  try {
    const response = await axios.post(
      `${basicURL}${aiGenerateQuestionsFromMaterialURL}`,
      formData,
      {
        headers: { Authorization: authHeader },
        onUploadProgress: (progressEvent) => {
          if (progressEvent?.total) {
            setProgress(progressEvent.loaded / progressEvent.total);
          }
        },
        timeout: 5 * 60 * 1000,
      },
    );
    return response?.data;
  } catch (err) {
    // axios throws for non-2xx — surface the backend's message verbatim so
    // the wizard can render "extraction failed" / "insufficient content"
    // distinct from a generic network error.
    if (err?.response?.data) return err.response.data;
    return {
      status: err?.response?.status || 0,
      message: err?.message || "Network error while generating questions.",
    };
  }
};
